import { RedoButton } from "@redotech/arbiter/buttons/redo-button";
import { Flex } from "@redotech/arbiter/flex";
import { RedoModal } from "@redotech/arbiter/modal/redo-modal";
import { RedoHorizontalTabs } from "@redotech/arbiter/tabs/redo-horizontal-tabs";
import { Text } from "@redotech/arbiter/text";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { Carriers } from "@redotech/redo-fulfillment-carrier-common/carrier-service-enums";
import { carrierToLabel } from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import {
  DEFAULT_DYNAMIC_RATE_PRICING_RULES,
  DynamicRatePricingEnabledCarriers,
} from "@redotech/redo-fulfillment-carrier-common/dynamic-rate-pricing";
import {
  LogoSize,
  ShippingLogo,
} from "@redotech/redo-web/fulfillment/common/shipping-logo";
import { InputSchema } from "@redotech/ui/form";
import { memo, useState } from "react";
import { DynamicRatePricingExample } from "./dynamic-rate-pricing-example";
import { DynamicRatePricingRuleRow } from "./dynamic-rate-pricing-rule-row";
import * as styles from "./dynamic-rate-pricing-rules-modal.module.css";
import {
  hasMixedGroupRules,
  pricingServiceGroups,
  rulesForGroup,
  serviceKey,
  updateGroupRules,
} from "./dynamic-rate-pricing-service-groups";
import type { dynamicRatePricingSettingsForm } from "./dynamic-rate-pricing-settings-form";

import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";
import { TeamContext } from "../../team";

const tabs = DynamicRatePricingEnabledCarriers.options.map((carrier) => ({
  key: carrier,
  label: carrierToLabel[carrier],
  icon: <ShippingLogo carrier={carrier} size={LogoSize.SMALL} />,
}));

interface DynamicRatePricingRulesModalProps {
  disabled: boolean;
  input: InputSchema.Input<typeof dynamicRatePricingSettingsForm>;
  isOpen: boolean;
  onClose(): void;
}

export const DynamicRatePricingRulesModal = memo(
  function DynamicRatePricingRulesModal({
    disabled,
    input,
    isOpen,
    onClose,
  }: DynamicRatePricingRulesModalProps) {
    const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
    const team = useRequiredContext(TeamContext);
    const teamId = team._id.toString();
    const [retry, setRetry] = useState(0);
    const services = useLoad(
      async (signal) => {
        if (!isOpen) {
          return undefined;
        }
        const result = await rpcClient.getCarrierServiceCombinations(
          { teamId },
          { signal },
        );
        return { teamId, options: result.options };
      },
      [rpcClient, teamId, isOpen, retry],
    );
    const serviceOptions =
      !services.pending && services.value?.teamId === teamId
        ? services.value.options
        : [];
    const servicesUnavailable =
      services.pending ||
      !services.value ||
      services.value.teamId !== teamId ||
      !!services.error;
    const [carrier, setCarrier] = useState<(typeof tabs)[number]["key"]>(
      Carriers.DHL_ECS,
    );
    const [showIgnored, setShowIgnored] = useState(false);
    const rules = input.inputs.rules;
    const groups = pricingServiceGroups.filter(
      (group) => group.carrier === carrier,
    );
    const active = groups.filter(
      (group) => rulesForGroup(group, rules.value).length > 0,
    );
    const ignored = groups.filter(
      (group) => rulesForGroup(group, rules.value).length === 0,
    );
    return (
      <RedoModal
        className={styles.modal}
        isOpen={isOpen}
        modalSize="xl"
        onModalCloseRequested={onClose}
        title="Dynamic rate pricing rules"
      >
        <Flex dir="column" gap="md">
          <DynamicRatePricingExample />
          <Flex dir="column" pt="md">
            <RedoHorizontalTabs
              drawBottomLine
              pl="none"
              selectedTab={carrier}
              setSelectedTab={setCarrier}
              size="sm"
              tabs={tabs}
            />
          </Flex>
          {services.error && (
            <Flex align="center" gap="sm">
              <Text fontSize="sm" textColor="error">
                Could not load merchant carrier services.
              </Text>
              <RedoButton
                hierarchy="secondary"
                onClick={() => setRetry(retry + 1)}
                size="xs"
                text="Retry"
              />
            </Flex>
          )}
          {!servicesUnavailable && serviceOptions.length === 0 && (
            <Text fontSize="sm" textColor="secondary">
              No eligible carrier services are configured for this merchant.
            </Text>
          )}
          {active.map((group) => {
            const value = rulesForGroup(group, rules.value)[0];
            return (
              value && (
                <DynamicRatePricingRuleRow
                  benchmarkOptions={serviceOptions}
                  benchmarksDisabled={servicesUnavailable}
                  disabled={disabled}
                  group={group}
                  key={group.key}
                  mixed={hasMixedGroupRules(group, rules.value)}
                  onChange={(value) =>
                    rules.setValue(updateGroupRules(rules.value, group, value))
                  }
                  value={value}
                />
              )
            );
          })}
          {active.length === 0 && (
            <Text fontSize="sm" textColor="secondary">
              No active mappings for this carrier.
            </Text>
          )}
          {ignored.length > 0 && (
            <RedoButton
              hierarchy="tertiary"
              onClick={() => setShowIgnored(!showIgnored)}
              size="sm"
              text={`${showIgnored ? "Hide" : "Show"} ignored services (${ignored.length})`}
            />
          )}
          {showIgnored &&
            ignored.map((group) => (
              <Flex
                align="center"
                gap="sm"
                justify="space-between"
                key={group.key}
              >
                <Text fontSize="sm" textColor="secondary">
                  {group.label}
                </Text>
                <RedoButton
                  disabled={disabled}
                  hierarchy="secondary"
                  onClick={() => {
                    const source = group.services[0];
                    if (!source) {
                      return;
                    }
                    const defaults = DEFAULT_DYNAMIC_RATE_PRICING_RULES.find(
                      (rule) => serviceKey(rule) === serviceKey(source),
                    );
                    rules.setValue(
                      updateGroupRules(rules.value, group, {
                        source,
                        benchmarks: defaults?.benchmarks ?? [],
                        minBeatRateBy: defaults?.minBeatRateBy ?? 0.01,
                        maxBeatRateBy: defaults?.maxBeatRateBy ?? 0.3,
                      }),
                    );
                  }}
                  size="xs"
                  text="Add mapping"
                />
              </Flex>
            ))}
          <Text fontSize="xs" textColor="secondary">
            Return services and international services are ignored by default.
          </Text>
          {input.errors.length > 0 && (
            <Text fontSize="sm" textColor="error">
              {input.errors.join(". ")}
            </Text>
          )}
        </Flex>
      </RedoModal>
    );
  },
);
